import { studioCatalogueAuthHeaders } from "./catalogueSecurityApi";
import type { ViewerEvent } from "../../pages/catalog/ecomerce/engagementQueue";

type ApiEnvelope<T> = { success?: boolean; message?: string; data?: T };

const apiBase = (): string => (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

const engagementPath = (catalogueId: string, suffix = ""): string =>
  `${apiBase()}/catalogue-engagement/${encodeURIComponent(catalogueId)}${suffix}`;

export interface CatalogueViewerPiece {
  productUniqueId: string;
  name?: string | null;
  seconds: number;
  views: number;
}

export interface CatalogueViewerRow {
  viewerUserId: string;
  name?: string | null;
  phone?: string | null;
  /** true = confirmed by OTP, false = typed only, null = invited number. */
  phoneVerified?: boolean | null;
  linkToken?: string | null;
  opens: number;
  firstViewDate?: string | null;
  lastViewDate?: string | null;
  lastActiveAt?: string | null;
  activeSeconds: number;
  productViews: number;
  piecesViewed: number;
  topPieces?: CatalogueViewerPiece[];
}

export async function fetchCatalogueViewers(
  catalogueId: string,
  limit = 200
): Promise<ApiEnvelope<CatalogueViewerRow[]>> {
  const res = await fetch(engagementPath(catalogueId, `/viewers?limit=${limit}`), {
    headers: studioCatalogueAuthHeaders(),
  });
  const text = await res.text();
  if (!text) return { success: res.ok };
  try {
    return JSON.parse(text) as ApiEnvelope<CatalogueViewerRow[]>;
  } catch {
    return { success: false, message: `Request failed (HTTP ${res.status}).` };
  }
}

/**
 * Fire-and-forget. The body goes as text/plain so `sendBeacon` (no preflight) and `fetch` share
 * one shape; the session rides in the body for the same reason. Never throws.
 */
export function sendCatalogueViewerEvents(
  catalogueId: string,
  payload: { sessionToken: string; linkToken: string; events: ViewerEvent[] },
  options?: { beacon?: boolean }
): void {
  if (!payload.events.length) return;
  const url = engagementPath(catalogueId, "/events");
  const body = JSON.stringify(payload);
  try {
    if (options?.beacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))) return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* analytics never blocks browsing */
  }
}
